import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Error from '@mui/icons-material/Error';
import { Alert, AlertColor, Box, Button, Paper, Stack } from '@mui/material';
import { useState } from 'react';

import { Piece, PieceAction, PieceAnnotation, TrackingNumberAnnouncementStatus, Workflow } from '@apiContract';
import Title from '@components/DrawerContent/Title';

import { clearServiceAnnotations } from '@utils/annotations';

import TrackForm from './components/TrackForm';
import TrackInput from './components/TrackInput';
import { sxPaper } from './styles';
import { getAlert, getLockersAlert } from './utils';

type BarcodeProps = {
  onClose: () => void;
  piece?: Piece;
  pieceAnnotations: PieceAnnotation[] | null;
  workflow: Workflow;
  onCreate?: (piceAnnotation: PieceAnnotation) => void;
  noAddButton?: boolean;
  detected?: boolean;
};

const Barcode = ({ onClose, piece, pieceAnnotations, workflow, onCreate, noAddButton, detected }: BarcodeProps) => {
  const [open, setOpen] = useState(false);

  const isLoading = !detected && !piece?.events.some((event) => event.action === PieceAction.BarcodeDetectionComplete);

  const filteredAnnotations = clearServiceAnnotations(pieceAnnotations);
  const hasRecipient = !!piece?.recipient;

  const alert =
    workflow === Workflow.Lockers
      ? getLockersAlert(pieceAnnotations, filteredAnnotations, hasRecipient)
      : getAlert(filteredAnnotations);

  const isAnyValid = pieceAnnotations?.some(
    (annotation) =>
      annotation.pieceAnnotationsTrackingNumber?.announcementStatus === TrackingNumberAnnouncementStatus.Confirmed,
  );

  const allAnnouncementsFinished = filteredAnnotations
    .filter((a) => a.pieceAnnotationsTrackingNumber?.validationStatus === 0)
    .every(
      (a) =>
        a.pieceAnnotationsTrackingNumber?.announcementStatus !== TrackingNumberAnnouncementStatus.None &&
        a.pieceAnnotationsTrackingNumber?.announcementStatus !== TrackingNumberAnnouncementStatus.Sent,
    );

  return (
    <Paper sx={sxPaper}>
      <Title title="Tracking Numbers" onClose={onClose} isLoading={isLoading} />
      <Stack spacing={3} p={3}>
        {!isLoading && alert && (
          <Alert icon={alert.severity === 'error' ? <Error /> : undefined} severity={alert.severity as AlertColor}>
            {alert.text}
          </Alert>
        )}
        {filteredAnnotations.map((item) => (
          <TrackInput
            isAnyValid={isAnyValid}
            key={item.id}
            item={item}
            workflow={workflow}
            hasRecipient={hasRecipient}
            allAnnouncementsFinished={allAnnouncementsFinished}
          />
        ))}

        {!noAddButton && (
          <Box display="flex" justifyContent="flex-start">
            <Button variant="text" startIcon={<AddRoundedIcon />} onClick={() => setOpen((prev) => !prev)}>
              Add Tracking Number
            </Button>
          </Box>
        )}
        {open && piece && <TrackForm pieceId={piece.id} onClose={() => setOpen(false)} onCreate={onCreate} />}
      </Stack>
    </Paper>
  );
};

export default Barcode;
